import { Controller } from '@hotwired/stimulus'
import { DeclarativeWebPushManager } from '../notifications/declarative_web_push'
import { FirebaseNotificationManager } from '../notifications/firebase_notifications'
import { NotificationAvailability } from '../lib/device_detection'
import { ClassLogger } from '../lib/logging/class_logger'
import {
  subscribe_email_user_notification_preferences,
  unsubscribe_email_user_notification_preferences,
  notification,
  remove_token_notifications
} from '../routes'

const logger = new ClassLogger('NotificationPreferencesController')
const log = logger.log.bind(logger)
const error = logger.error.bind(logger)

export default class extends Controller {
  static targets = [
    'mobileCheckbox',
    'mobileCheckboxRow',
    'mobileCheckboxLabel',
    'mobileInfo',
    'webCheckbox',
    'webCheckboxRow',
    'webCheckboxLabel',
    'webInfo',
    'statusMessage',
    'deviceList',
    'deviceLoading',
    'deviceContainer',
    'deviceEmpty',
    'deviceItems',
    'totalCount'
  ]

  static values = {
    userId: String,
    pwaInstallUrl: String
  }

  connect () {
    log('Notification preferences controller connected')
    this.checkEligibility()
    this.loadDevices()
  }

  async checkEligibility () {
    log('Checking notification eligibility')

    const DeviceDetection =
      (await import('../lib/device_detection')).DeviceDetection

    // Detect device context
    const isIOSPWA = DeviceDetection.isIOSPWA()
    const isAndroidPWA = DeviceDetection.isAndroidPWA()
    const isDesktopBrowser = !DeviceDetection.isMobileDevice() &&
      (DeviceDetection.isChromeDesktop() || DeviceDetection.isFirefoxDesktop() || DeviceDetection.isSafariDesktop())
    const isMobileBrowser = DeviceDetection.isMobileDevice() && !isIOSPWA && !isAndroidPWA

    log('Device context:', {
      isIOSPWA,
      isAndroidPWA,
      isDesktopBrowser,
      isMobileBrowser
    })

    // Handle iOS PWA - Show mobile notifications only
    if (isIOSPWA) {
      this.showMobileNotifications('push notifications')
      this.hideWebNotifications()
      return
    }

    // Handle Android PWA - Show web notifications only (renamed to "Push notifications")
    if (isAndroidPWA) {
      this.hideMobileNotifications()
      this.showWebNotifications('push notifications')
      return
    }

    // Handle Desktop browsers - Show web notifications only
    if (isDesktopBrowser) {
      this.hideMobileNotifications()
      this.showWebNotifications('browser notifications')
      return
    }

    // Handle mobile browsers - Show install PWA message
    if (isMobileBrowser) {
      this.showMobileBrowserMessage()
      return
    }

    // Fallback - try both but likely disabled
    this.tryBothNotifications()
  }

  async showMobileNotifications (labelText = 'push notifications') {
    const mobileAvailability =
      NotificationAvailability.canUseDeclarativeWebPush(this.pwaInstallUrlValue)
    log('Mobile (Declarative Web Push) supported:', mobileAvailability.available)

    if (mobileAvailability.available) {
      this.mobileCheckboxTarget.disabled = false
      this.mobileCheckboxTarget.classList.remove('opacity-50')
      const label = this.mobileCheckboxTarget.nextElementSibling
      if (label) {
        label.classList.remove('text-gray-500')
        label.classList.add('text-gray-700')
      }
      this.mobileInfoTarget.textContent = ''
      if (this.hasMobileCheckboxLabelTarget) {
        this.mobileCheckboxLabelTarget.textContent = labelText
      }

      console.log('🎯 [Prefs] About to sync WebPush subscription...')
      const result = await DeclarativeWebPushManager.syncSubscription(this.current_ecosystem_id())
      if (result.orphanCleaned) {
        console.log('🧹 [Prefs] Orphaned subscription cleaned before showing preferences')
      }
      console.log('🎯 [Prefs] WebPush sync complete, checking subscription...')
      const hasSubscription = await DeclarativeWebPushManager.hasEndpointForEcosystem(this.current_ecosystem_id())
      console.log('🎯 [Prefs] Final hasSubscription result:', hasSubscription)
      this.mobileCheckboxTarget.checked = hasSubscription
      this.updateCheckboxUI(this.mobileCheckboxTarget, hasSubscription)
      this.mobileCheckboxTarget.disabled = false
      this.loadDevices()
    } else {
      const reason = mobileAvailability.reason
      if (reason.html) {
        this.mobileInfoTarget.innerHTML = reason.message
      } else {
        this.mobileInfoTarget.textContent = reason.message
      }
    }
  }

  hideMobileNotifications () {
    if (this.hasMobileCheckboxRowTarget) {
      this.mobileCheckboxRowTarget.style.display = 'none'
    }
  }

  async showWebNotifications (labelText = 'browser notifications') {
    const webAvailability = NotificationAvailability.canUseFirebase()
    log('Web (Firebase) supported:', webAvailability.available)

    if (webAvailability.available) {
      this.webCheckboxTarget.classList.remove('opacity-50')
      const label = this.webCheckboxTarget.nextElementSibling
      if (label) {
        label.classList.remove('text-gray-500')
        label.classList.add('text-gray-700')
      }
      this.webInfoTarget.textContent = ''

      // Update label text based on context
      if (this.hasWebCheckboxLabelTarget) {
        this.webCheckboxLabelTarget.textContent = labelText
      }

      console.log('🎯 [Prefs] About to sync Firebase token...')
      const result = await FirebaseNotificationManager.syncToken(this.current_ecosystem_id())
      if (result.orphanCleaned) {
        console.log('🧹 [Prefs] Orphaned Firebase token cleaned before showing preferences')
      }
      console.log('🎯 [Prefs] Firebase sync complete, checking token...')
      const hasToken = await FirebaseNotificationManager.hasTokenForEcosystem(this.current_ecosystem_id())
      console.log('🎯 [Prefs] Final hasToken result:', hasToken)
      this.updateCheckboxUI(this.webCheckboxTarget, hasToken)
      this.webCheckboxTarget.disabled = false
      this.loadDevices()
    } else {
      this.webInfoTarget.textContent = webAvailability.reason
    }
  }

  hideWebNotifications () {
    if (this.hasWebCheckboxRowTarget) {
      this.webCheckboxRowTarget.style.display = 'none'
    }
  }

  async showMobileBrowserMessage () {
    // Hide both checkboxes
    this.hideMobileNotifications()
    this.hideWebNotifications()

    // Show install PWA message in mobile section
    if (this.hasMobileInfoTarget) {
      const mobileAvailability =
        NotificationAvailability.canUseDeclarativeWebPush(this.pwaInstallUrlValue)
      const reason = mobileAvailability.reason
      if (reason.html) {
        this.mobileInfoTarget.innerHTML = reason.message
      } else {
        this.mobileInfoTarget.textContent = reason.message
      }
    }

    // Show install PWA message in web section
    if (this.hasWebInfoTarget) {
      this.webInfoTarget.textContent = 'Install app to manage notifications'
    }
  }

  async tryBothNotifications () {
    // Fallback logic - try both but they'll likely be disabled
    const mobileAvailability =
      NotificationAvailability.canUseDeclarativeWebPush(this.pwaInstallUrlValue)
    const webAvailability = NotificationAvailability.canUseFirebase()

    if (mobileAvailability.available) {
      await this.showMobileNotifications()
    } else {
      const reason = mobileAvailability.reason
      if (reason.html) {
        this.mobileInfoTarget.innerHTML = reason.message
      } else {
        this.mobileInfoTarget.textContent = reason.message
      }
    }

    if (webAvailability.available) {
      await this.showWebNotifications()
    } else {
      this.webInfoTarget.textContent = webAvailability.reason
    }
  }

  async toggleMobile (event) {
    if (event.target.disabled) {
      log('toggleMobile() - Checkbox is disabled, ignoring toggle')
      return
    }

    const isChecked = !(event.target.dataset.checked === 'true')
    log('toggleMobile() - Called with checked:', isChecked)
    event.target.disabled = true

    try {
      if (isChecked) {
        const subscription = await DeclarativeWebPushManager.subscribe(this.current_ecosystem_id())
        if (subscription) {
          this.showStatus('Successfully subscribed to push notifications!', 'success')
          this.loadDevices()
          this.updateCheckboxUI(event.target, isChecked)
        } else {
          event.target.checked = false
          this.showStatus('Error creating push notifications subscription.', 'error')
        }
      } else {
        if (!confirm('Are you sure you want to stop push notifications on this device?')) {
          log('toggleMobile() - User cancelled unsubscribe')
          return
        }

        await DeclarativeWebPushManager.unsubscribeFromEcosystem(this.current_ecosystem_id())
        this.showStatus('Successfully unsubscribed.', 'success')
        this.loadDevices()
        this.updateCheckboxUI(event.target, isChecked)
      }
    } catch (err) {
      error('Failed to toggle mobile notifications:', err)
      event.target.checked = !isChecked
      this.showStatus(`Error: ${err.message}`, 'error')
    } finally {
      event.target.disabled = false
    }
  }

  async toggleWeb (event) {
    if (event.target.disabled) {
      log('toggleWeb() - Checkbox is disabled, ignoring toggle')
      return
    }

    const isChecked = !(event.target.dataset.checked === 'true')
    log('toggleWeb() - Called with checked:', isChecked)
    event.target.disabled = true

    try {
      if (isChecked) {
        log('toggleWeb() - Subscribing to web notifications...')
        const result = await FirebaseNotificationManager.subscribe(this.current_ecosystem_id())
        log('toggleWeb() - Subscribe successful:', result)
        this.showStatus(result.message, 'success')
        this.loadDevices()
        this.updateCheckboxUI(event.target, isChecked)
      } else {
        log('toggleWeb() - Unsubscribing from web notifications...')
        if (!confirm('Are you sure you want to stop notifications on this device?')) {
          log('toggleWeb() - User cancelled unsubscribe')
          return
        }

        await FirebaseNotificationManager.unsubscribe(this.current_ecosystem_id())
        this.showStatus('Successfully unsubscribed.', 'success')
        log('toggleWeb() - Unsubscribe successful')
        this.loadDevices()
        this.updateCheckboxUI(event.target, isChecked)
      }
    } catch (err) {
      error('toggleWeb() - Failed to toggle web notifications:', err)
      event.target.dataset.checked = !isChecked
      this.showStatus(`Error: ${err.message}`, 'error')
    } finally {
      event.target.disabled = false
      log('toggleWeb() - Completed, checkbox enabled')
    }
  }

  async toggleEmail (event) {
    const isChecked = event.target.checked

    event.target.disabled = true

    try {
      const userId = this.userIdValue
      const endpoint = isChecked ? 'subscribe_email' : 'unsubscribe_email'

      if (!isChecked) {
        if (!confirm('Are you sure you want to stop email notifications?')) {
          event.target.checked = true
          event.target.disabled = false
          return
        }
      }

      const routeHelper = endpoint === 'subscribe_email'
        ? subscribe_email_user_notification_preferences
        : unsubscribe_email_user_notification_preferences

      const response = await fetch(routeHelper(userId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      const data = await response.json()

      if (response.ok) {
        this.showStatus(data.message, 'success')
      } else {
        event.target.checked = !isChecked
        this.showStatus(data.error || 'Failed to update email notifications', 'error')
      }
    } catch (err) {
      error('Failed to toggle email notifications:', err)
      event.target.checked = !isChecked
      this.showStatus(`Error: ${err.message}`, 'error')
    } finally {
      event.target.disabled = false
    }
  }

  showStatus (message, type) {
    if (!this.hasStatusMessageTarget) return

    const containerEl = this.statusMessageTarget
    const innerEl = containerEl.firstElementChild

    innerEl.textContent = message
    innerEl.classList.remove(
      'bg-custom-green-light', 'text-custom-green', 'border-custom-green',
      'bg-custom-red-light', 'text-custom-red', 'border-custom-red'
    )

    if (type === 'success') {
      innerEl.classList.add('bg-custom-green-light', 'text-custom-green', 'border-custom-green')
    } else {
      innerEl.classList.add('bg-custom-red-light', 'text-custom-red', 'border-custom-red')
    }

    containerEl.classList.remove('hidden')

    requestAnimationFrame(() => {
      containerEl.classList.remove('opacity-0', 'translate-y-[-1rem]')
      containerEl.classList.add('opacity-100', 'translate-y-0')
    })

    setTimeout(() => {
      containerEl.classList.remove('opacity-100', 'translate-y-0')
      containerEl.classList.add('opacity-0', 'translate-y-[-1rem]')

      setTimeout(() => {
        containerEl.classList.add('hidden')
      }, 300)
    }, 8000)
  }

  async loadDevices () {
    if (!this.hasDeviceListTarget) return

    const ecosystemId = this.current_ecosystem_id()
    console.log(`Loading devices for ecosystem ID: ${ecosystemId}`)

    try {
      const url = `/users/${this.userIdValue}/notification_preferences/devices?ecosystem_id=${ecosystemId}`
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to load devices: ${response.status}`)
      }

      const data = await response.json()
      this.updateTotalCount(data.devices.length)
    } catch (err) {
      error('Failed to load devices:', err)
      if (this.hasDeviceLoadingTarget) {
        this.deviceLoadingTarget.textContent = 'Failed to load devices.'
      }
    }
  }

  async unsubscribeDevice (event) {
    const button = event.currentTarget
    const tokenId = button.dataset.tokenId
    const deviceType = button.dataset.deviceType

    if (!confirm(`Are you sure you want to remove notifications from this ${deviceType}?`)) {
      return
    }

    // Disable button and show loading state
    button.disabled = true
    button.textContent = 'Removing...'

    try {
      const response = await fetch(notification(tokenId), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to remove device: ${response.status}`)
      }

      const data = await response.json()
      this.showStatus(data.message || 'Device removed successfully', 'success')

      // Reload devices list
      await this.loadDevices()
    } catch (err) {
      error('Failed to remove device:', err)
      this.showStatus(`Error: ${err.message}`, 'error')

      // Restore button state
      button.disabled = false
      button.textContent = 'Remove'
    }
  }

  async removeBackendToken (tokenType) {
    try {
      let subscription = {}

      if (tokenType === 'declarative_web_push') {
        // Get current subscription for Declarative Web Push
        const currentSubscription = await window.DeclarativeWebPush.getSubscription()
        if (currentSubscription) {
          subscription = {
            endpoint: currentSubscription.endpoint
          }
        }
      } else if (tokenType === 'firebase') {
        // Get current token for Firebase
        const currentToken = await FirebaseNotificationManager.getTokenForEcosystem(this.current_ecosystem_id())
        if (currentToken) {
          subscription = {
            token: currentToken
          }
        }
      }

      if (!subscription.endpoint && !subscription.token) {
        log('No active subscription found to remove from backend')
        return
      }

      const response = await fetch(remove_token_notifications(), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          subscription,
          token_type: tokenType,
          ecosystem_id: this.current_ecosystem_id()
        })
      })

      if (!response.ok) {
        throw new Error(`Failed to remove backend token: ${response.status}`)
      }

      const data = await response.json()
      log('Backend token removed successfully:', data)
    } catch (err) {
      error('Failed to remove backend token:', err)
      throw err // Re-throw to let caller handle
    }
  }

  updateTotalCount (count = null) {
    if (!this.hasTotalCountTarget) return

    // Always require explicit count from backend
    if (count !== null) {
      this.totalCountTargets.forEach(target => {
        target.textContent = count === 1 ? '1 device' : `${count} total devices`
      })
    } else {
      // Fallback: load devices to get accurate count
      this.loadDevices()
    }
  }

  updateCheckboxUI (checkbox, isChecked) {
    console.log('updateCheckboxUI - isChecked:', isChecked)

    checkbox.dataset.checked = isChecked
    const childSpan = checkbox.querySelector('span')

    if (isChecked) {
      checkbox.classList.add('bg-secondary')
      checkbox.classList.remove('bg-neutral-600')
      childSpan.classList.add('translate-x-5')
      childSpan.classList.remove('translate-x-0')
    } else {
      checkbox.classList.remove('bg-secondary')
      checkbox.classList.add('bg-neutral-600')
      childSpan.classList.add('translate-x-0')
      childSpan.classList.remove('translate-x-5')
    }
  }

  current_ecosystem_id () {
    return document.querySelector('meta[name="ecosystem_id"]').content
  }
}
